import { React, useEffect, useRef, useState } from "react";
import { Modal, MenuItem, FormControl, Select } from "@mui/material";
import Swal from "sweetalert2";
import { LoaderScreen } from "../../components/LoaderScreen";
import request from "../../../Helpers/API";
import { FormatMoneyInt } from "../../../Helpers/FormatNumber";

export const CapturaEstimacionModal = ({ open, onClose, sucursales = [], onSaved }) => {
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const [sucursalSeleccionada, setSucursalSeleccionada] = useState("");
    const [detalle, setDetalle] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (!open) return;
        setSucursalSeleccionada("");
        setDetalle([]);
    }, [open]);

    useEffect(() => {
        if (!sucursalSeleccionada) {
            setDetalle([]);
            return;
        }
        GetDetalle(sucursalSeleccionada);
    }, [sucursalSeleccionada]);

    const GetDetalle = async (sucursal) => {
        setLoader((prev) => ({ ...prev, mostrar: true }));
        try {
            const result = await request.get(
                `EstimacionVentas/ObtenerEstimacionesVentasSucursal?sucursal=${sucursal}`
            );
            setDetalle(result || []);
            setLoader((prev) => ({ ...prev, mostrar: false }));
        } catch {
            setLoader((prev) => ({ ...prev, mostrar: false }));
            Swal.fire("ESTIMACIÓN DE VENTAS", "No se pudo cargar la información de la sucursal", "error");
        }
    };

    // Muestra el número formateado con miles cuando no está en foco
    const getDisplayValue = (item, index) => {
        if (item.ventas === "" || item.ventas === null || item.ventas === undefined) return "";
        if (focusedIndex === index) return String(item.ventas);
        return Number(item.ventas).toLocaleString("es-MX");
    };

    const handleChangeVentas = (index, valor) => {
        const raw = valor.replace(/[^0-9]/g, "");
        setDetalle((prev) =>
            prev.map((item, i) =>
                i === index
                    ? { ...item, ventas: raw === "" ? "" : Number(raw) }
                    : item
            )
        );
    };

    // Navega entre inputs con ArrowUp / ArrowDown
    const handleKeyDown = (e, index) => {
        if (e.key === "ArrowDown" && index < detalle.length - 1) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
        if (e.key === "ArrowUp" && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }
    };

    const validar = () => {
        if (!sucursalSeleccionada) {
            Swal.fire("ESTIMACIÓN DE VENTAS", "Debe seleccionar una sucursal", "warning");
            return false;
        }
        if (!detalle || detalle.length === 0) {
            Swal.fire("ESTIMACIÓN DE VENTAS", "No hay líneas para capturar", "warning");
            return false;
        }
        for (const item of detalle) {
            if (item.ventas === "" || item.ventas === null || item.ventas === undefined) {
                Swal.fire("ESTIMACIÓN DE VENTAS", `Debe capturar la venta estimada de ${item.linea}`, "warning");
                return false;
            }
            if (Number(item.ventas) < Number(item.vta_origen)) {
                Swal.fire(
                    "ESTIMACIÓN DE VENTAS",
                    `La venta estimada de ${item.linea} no puede ser menor a ${FormatMoneyInt(item.vta_origen)}`,
                    "warning"
                );
                return false;
            }
        }
        return true;
    };

    const handleGuardar = () => {
        if (!validar()) return;

        setLoader((prev) => ({ ...prev, mostrar: true }));

        const post = {
            detalle: JSON.stringify(detalle),
        };

        request
            .post(`EstimacionVentas/GuardarEstimacionVentas`, post)
            .then(async () => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                await Swal.fire({
                    title: "ESTIMACIÓN DE VENTAS",
                    text: "Registro guardado con éxito",
                    icon: "success",
                    customClass: { container: "mi-superpoop" },
                });
                onSaved && onSaved();
                handleCancelar();
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire("ESTIMACIÓN DE VENTAS", "No se pudo guardar la estimación", "error");
            });
    };

    const handleCancelar = () => {
        setSucursalSeleccionada("");
        setDetalle([]);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleCancelar}>
            <div className="em-modal-root">
                <LoaderScreen loader={loader} />

                {/* Encabezado */}
                <div className="em-modal-head">
                    <div className="em-head-grid">
                        <div className="em-head-left" />
                        <div className="em-head-center">
                            <h2 className="em-title">CAPTURA DE ESTIMACIÓN</h2>
                        </div>
                        <div className="em-head-right">
                            <button className="em-close-btn" onClick={handleCancelar}>✕</button>
                        </div>
                    </div>
                    <div className="em-head-divider">
                        <div className="em-head-divider-dot" />
                    </div>
                </div>

                {/* Cuerpo */}
                <div className="em-modal-body">
                    <div className="em-modal-scroll scroll-4">
                        <div className="lead-form-group">
                            <label>Sucursal</label>
                            <FormControl size="small" fullWidth>
                                <Select
                                    displayEmpty
                                    value={sucursalSeleccionada}
                                    onChange={(e) => setSucursalSeleccionada(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Seleccione una sucursal</em>
                                    </MenuItem>
                                    {sucursales.map((s) => (
                                        <MenuItem key={s.idsucursal} value={s.idsucursal}>
                                            {s.descripcion}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {detalle && detalle.length > 0 && (
                            <table className="estimacion-modal-tabla">
                                <thead>
                                    <tr>
                                        <th>Línea</th>
                                        <th><div>Venta Estimada</div></th>
                                        <th><div>Objetivo</div></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalle.map((item, index) => (
                                        <tr key={item.idlinea}>
                                            <td className="celda-linea">{item.linea}</td>
                                            <td className="celda-importe">
                                                <input
                                                    ref={(el) => (inputRefs.current[index] = el)}
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={getDisplayValue(item, index)}
                                                    onFocus={(e) => {
                                                        setFocusedIndex(index);
                                                        e.target.select();
                                                    }}
                                                    onBlur={() => setFocusedIndex(null)}
                                                    onChange={(e) => handleChangeVentas(index, e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                                />
                                            </td>
                                            <td className="celda-importe">{FormatMoneyInt(item.objetivo)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="celda-total-label">TOTAL</td>
                                        <td className="celda-total">
                                            {FormatMoneyInt(
                                                detalle.reduce((s, i) => s + (Number(i.ventas) || 0), 0)
                                            )}
                                        </td>
                                        <td className="celda-total">
                                            {FormatMoneyInt(
                                                detalle.reduce((s, i) => s + (Number(i.objetivo) || 0), 0)
                                            )}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>

                    {/* Botones */}
                    <div className="em-modal-buttons">
                        <button className="em-btn-cancelar" onClick={handleCancelar}>
                            Cancelar
                        </button>
                        <button
                            className="em-btn-guardar"
                            onClick={handleGuardar}
                            disabled={loader.mostrar}
                        >
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CapturaEstimacionModal;
